import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, BarChart3, CheckCheck, ClipboardList, Loader2, MessageSquare, CheckCircle2, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiClient } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { getEducatorRouteBase, isEducatorRole, isParentRole } from '@/lib/educators';
import { NOTIFICATIONS_UPDATED_EVENT } from '@/utils/notifications';

const KIND_ICONS = {
  homework_assigned: ClipboardList,
  educator_feedback: MessageSquare,
  student_homework_answer: PenLine,
  student_homework_done: CheckCircle2,
  child_practice_completed: PenLine,
  child_streak_milestone: CheckCircle2,
  child_mock_finished: ClipboardList,
  child_homework_activity: PenLine,
  child_weekly_summary: BarChart3,
};

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  if (diffMs < 60_000) return 'только что';
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)} мин назад`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)} ч назад`;
  return d.toLocaleDateString('ru-RU');
}

export default function NotificationBell({ className = '' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiClient.get('/notifications/unread-count');
      setUnreadCount(res.data?.count || 0);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/notifications', { params: { limit: 20 } });
      setItems(res.data?.notifications || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60_000);
    const onUpdated = () => fetchUnreadCount();
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, onUpdated);
    return () => {
      clearInterval(interval);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, onUpdated);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) fetchUnreadCount();
  };

  const openNotification = async (item) => {
    try {
      if (!item.read) {
        await apiClient.post(`/notifications/${item.id}/read`);
        setUnreadCount((c) => Math.max(0, c - 1));
        setItems((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)),
        );
      }
    } catch {
      /* ignore */
    }
    setOpen(false);
    if (item.link) {
      navigate(item.link);
    }
  };

  const markAllRead = async () => {
    try {
      await apiClient.post('/notifications/read-all');
      setUnreadCount(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  };

  const hubLink = user?.role === 'student'
    ? '/dashboard'
    : isEducatorRole(user?.role)
      ? isParentRole(user?.role)
        ? getEducatorRouteBase(user?.role)
        : `${getEducatorRouteBase(user?.role)}/ai`
      : '/dashboard';

  const hubLabel =
    user?.role === 'student' || isParentRole(user?.role)
      ? 'Открыть кабинет'
      : 'ИИ-помощник';

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'relative rounded-full px-3 py-2 hover:bg-primary/12 hover:text-primary',
            className,
          )}
          aria-label="Уведомления"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(92vw,360px)] rounded-2xl p-0">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <p className="font-semibold text-sm">Уведомления</p>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-primary hover:underline flex items-center gap-1"
              aria-label="Отметить все уведомления прочитанными"
            >
              <CheckCheck size={14} />
              Прочитать все
            </button>
          )}
        </div>
        <div className="max-h-[min(60vh,320px)] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8 px-4">
              Пока нет уведомлений
            </p>
          ) : (
            items.map((item) => {
              const Icon = KIND_ICONS[item.kind] || Bell;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openNotification(item)}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-border/40 hover:bg-muted/50 transition-colors',
                    !item.read && 'bg-primary/[0.04]',
                  )}
                >
                  <div className="flex gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">{item.title}</p>
                      {item.body && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 whitespace-pre-wrap">
                          {item.body}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {formatWhen(item.created_at)}
                      </p>
                    </div>
                    {!item.read && (
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
        <div className="border-t border-border/60 px-4 py-2.5">
          <Link
            to={hubLink}
            onClick={() => setOpen(false)}
            className="text-xs text-muted-foreground hover:text-primary"
          >
            {hubLabel}
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
